// One element's record and parameters, as the model view's Component
// Details shows them - the same groups, the same field formatting
// (helpers/recordFields), in a panel narrow enough to float.
import React, { useState } from "react";
import { Spin } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { recordFieldRows } from "helpers/recordFields";

// The model view's order and its omissions (pages/ModelViewer).
export const PARAM_GROUPS = [
  "BuiltInParameters", "EquipmentParameters", "FamilyParameters",
  "GlobalParameters", "ProjectParameters", "SharedParameters",
];
const RECORD_OMIT = ["$id", "TotalPrice", "UnitPrice", "Children", "children"];
const HEAD = ["CategoryName", "FamilyName", "TypeName", "Name", "Level", "LevelName"];

function Rows({ data, omit }) {
  const rows = recordFieldRows(data, { omit });
  if (!rows.length) return <div style={{ padding: "4px 10px", color: "#999", fontSize: 12 }}>Nothing recorded</div>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} style={{ borderBottom: "1px solid #f3f3f3" }}>
            <td title={r.label} style={{ padding: `3px 6px 3px ${10 + (r.depth || 0) * 12}px`, color: "#666", width: "45%", verticalAlign: "top", wordBreak: "break-word" }}>
              {r.label}
            </td>
            <td title={r.text} style={{ padding: "3px 10px 3px 4px", color: "#111", wordBreak: "break-word" }}>{r.text}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Section({ title, open: startOpen = false, children }) {
  const [open, setOpen] = useState(startOpen);
  return (
    <div style={{ borderTop: "1px solid #eee" }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={{
        display: "flex", alignItems: "center", gap: 6, width: "100%", border: "none", background: "#fcfcfd",
        padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "left",
      }}>
        {open ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
        {title}
      </button>
      {open && children}
    </div>
  );
}

// `info` is helpers/elementRecords' answer; `fallback` the search index's
// row for the same element, shown while the record loads or when the
// export has none.
export default function ElementProperties({ info, loading = false, fallback = null, extra = null }) {
  const record = info && info.record;
  const params = info && info.params;
  const head = record
    ? HEAD.map((k) => record[k]).filter(Boolean)
    : fallback ? [fallback.category, fallback.family, fallback.typeName, fallback.name, fallback.level].filter(Boolean) : [];
  return (
    <div>
      <div style={{ padding: "8px 10px", fontSize: 12.5 }}>
        <div style={{ fontWeight: 600 }}>{head.slice(0, 3).join(" · ") || "Element"}</div>
        {head.length > 3 && <div style={{ color: "#666" }}>{head.slice(3).join(" · ")}</div>}
        {info && info.uniqueId && <div style={{ color: "#999", fontSize: 11, marginTop: 2, wordBreak: "break-all" }}>{info.uniqueId}</div>}
        {info && info.document && <div style={{ color: "#999", fontSize: 11 }}>{info.document}</div>}
      </div>
      {loading && !record && <div style={{ padding: 12, textAlign: "center" }}><Spin size="small" /> <span style={{ fontSize: 12, color: "#888" }}>Reading the element's record…</span></div>}
      {info && info.error && <div style={{ padding: "4px 10px 8px", fontSize: 12, color: "#b45309" }}>{info.error}</div>}
      {!loading && info && !record && !info.error && (
        <div style={{ padding: "4px 10px 8px", fontSize: 12, color: "#888" }}>The export has no record for this element.</div>
      )}
      {record && (
        <Section title="Component details" open>
          <Rows data={record} omit={RECORD_OMIT} />
        </Section>
      )}
      {params && PARAM_GROUPS.filter((g) => params[g] && Object.keys(params[g]).length).map((g) => (
        <Section key={g} title={g.replace(/Parameters$/, " parameters")} open={g === "BuiltInParameters"}>
          <Rows data={params[g]} />
        </Section>
      ))}
      {extra}
    </div>
  );
}
